import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DeckLanguage, NewCardSortOrder } from "@glosor/shared";
import { api } from "../api";

const LANGUAGE_OPTIONS: { value: DeckLanguage | ""; label: string; description: string }[] = [
  {
    value: "",
    label: "None",
    description: "No dictionary lookup. Both the Korean and Swedish panels are disabled.",
  },
  {
    value: "sv",
    label: "Swedish (LEXIN)",
    description:
      "Shows the Swedish→Swedish dictionary (LEXIN \"Svenska ord\") under the front field: Swedish definition, English translation, examples and inflections.",
  },
  {
    value: "ko",
    label: "Korean (KRDict)",
    description:
      "Shows the Korean→English dictionary (KRDict) under the front field when you type Hangul.",
  },
];

const QUICK_BUMPS = [5, 10, 20];

const SORT_ORDER_LABELS: { value: NewCardSortOrder; label: string; description: string }[] = [
  {
    value: "card_type_then_random",
    label: "Card type, then random",
    description:
      "All front→back cards first (shuffled), then all back→front cards (shuffled). Keeps siblings apart while breaking the order you added notes in.",
  },
  {
    value: "random",
    label: "Random",
    description:
      "Fully shuffles every eligible new card. Front→back and back→front cards may interleave freely.",
  },
  {
    value: "random_note_then_card_type",
    label: "Random note, then card type",
    description:
      "Picks notes in random order and shows each note's front→back card right before its back→front card.",
  },
  {
    value: "card_type_then_order_gathered",
    label: "Card type, then order gathered",
    description:
      "All front→back cards first in the order you added them, then all back→front cards. Predictable but groups by direction.",
  },
  {
    value: "order_gathered",
    label: "Order gathered",
    description:
      "Strict insertion order — cards appear exactly as you added them. The old Glosor behavior.",
  },
];

export default function DeckOptions() {
  const { deckId } = useParams();
  const id = Number(deckId);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const decks = useQuery({ queryKey: ["decks"], queryFn: () => api.listDecks() });
  const deck = decks.data?.find((d) => d.id === id);

  const [name, setName] = useState("");
  const [perDay, setPerDay] = useState(20);
  const [bump, setBump] = useState(10);

  useEffect(() => {
    if (deck) {
      setName(deck.name);
      setPerDay(deck.newCardsPerDay);
    }
  }, [deck]);

  const saveName = useMutation({
    mutationFn: () => api.updateDeck(id, { name: name.trim() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["decks"] }),
  });

  const savePerDay = useMutation({
    mutationFn: () => api.updateDeck(id, { newCardsPerDay: perDay }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["decks"] }),
  });

  const saveSortOrder = useMutation({
    mutationFn: (value: NewCardSortOrder) => api.updateDeck(id, { newCardSortOrder: value }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decks"] });
      qc.invalidateQueries({ queryKey: ["study", id] });
    },
  });

  const saveLanguage = useMutation({
    mutationFn: (value: DeckLanguage | null) => api.updateDeck(id, { language: value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["decks"] }),
  });

  const bumpToday = useMutation({
    mutationFn: (amount: number) => api.increaseNewToday(id, amount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decks"] });
      qc.invalidateQueries({ queryKey: ["study", id] });
    },
  });

  const deleteDeck = useMutation({
    mutationFn: () => api.deleteDeck(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decks"] });
      navigate("/");
    },
  });

  if (!Number.isInteger(id)) return <div className="p-6 text-slate-500 dark:text-slate-400">Bad deck id.</div>;
  if (decks.isLoading) return <div className="p-6 text-slate-500 dark:text-slate-400">Loading…</div>;
  if (!deck) return <div className="p-6 text-slate-500 dark:text-slate-400">Deck not found.</div>;

  const effectiveCap = deck.newCardsPerDay + deck.extraNewToday;

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
      <header className="flex items-center gap-3">
        <button onClick={() => navigate(`/decks/${id}`)} className="btn-secondary">
          ←
        </button>
        <h1 className="text-2xl font-semibold truncate flex-1">Deck options</h1>
      </header>

      <section className="card-surface p-4 space-y-3">
        <label className="block text-sm font-medium">Name</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input flex-1"
          />
          <button
            type="button"
            onClick={() => saveName.mutate()}
            disabled={!name.trim() || name.trim() === deck.name || saveName.isPending}
            className="btn-primary"
          >
            Save
          </button>
        </div>
      </section>

      <section className="card-surface p-4 space-y-3">
        <label className="block text-sm font-medium">New cards per day</label>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Maximum new cards introduced from this deck each day.
        </p>
        <div className="flex gap-2">
          <input
            type="number"
            min={0}
            max={9999}
            value={perDay}
            onChange={(e) => setPerDay(Math.max(0, Math.min(9999, Math.floor(Number(e.target.value) || 0))))}
            className="input w-32"
          />
          <button
            type="button"
            onClick={() => savePerDay.mutate()}
            disabled={perDay === deck.newCardsPerDay || savePerDay.isPending}
            className="btn-primary"
          >
            Save
          </button>
        </div>
      </section>

      <section className="card-surface p-4 space-y-3">
        <label className="block text-sm font-medium">New card order</label>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          How the deck's daily queue of new cards is arranged. Anki-style.
        </p>
        <select
          value={deck.newCardSortOrder}
          onChange={(e) => saveSortOrder.mutate(e.target.value as NewCardSortOrder)}
          disabled={saveSortOrder.isPending}
          className="input w-full"
        >
          {SORT_ORDER_LABELS.map((opt) => (
            <option key={opt.value} value={opt.value} title={opt.description}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {SORT_ORDER_LABELS.find((o) => o.value === deck.newCardSortOrder)?.description}
        </p>
      </section>

      <section className="card-surface p-4 space-y-3">
        <label className="block text-sm font-medium">Dictionary language</label>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Which dictionary the lookup panel under the front field uses when adding cards.
        </p>
        <select
          value={deck.language ?? ""}
          onChange={(e) => saveLanguage.mutate((e.target.value || null) as DeckLanguage | null)}
          disabled={saveLanguage.isPending}
          className="input w-full"
        >
          {LANGUAGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} title={opt.description}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {LANGUAGE_OPTIONS.find((o) => (o.value || null) === (deck.language ?? null))?.description}
        </p>
      </section>

      <section className="card-surface p-4 space-y-3">
        <div>
          <div className="text-sm font-medium">Today</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Studied <span className="text-slate-900 dark:text-slate-200">{deck.newCardsStudiedToday}</span> of{" "}
            <span className="text-slate-900 dark:text-slate-200">{effectiveCap}</span> new cards today
            {deck.extraNewToday > 0 && (
              <span className="text-emerald-600 dark:text-emerald-400"> (+{deck.extraNewToday} bump)</span>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
            Add more for today
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="number"
              min={1}
              max={9999}
              value={bump}
              onChange={(e) => setBump(Math.max(1, Math.min(9999, Math.floor(Number(e.target.value) || 0))))}
              className="input w-24"
            />
            <button
              type="button"
              onClick={() => bumpToday.mutate(deck.extraNewToday + bump)}
              disabled={bumpToday.isPending}
              className="btn-primary"
            >
              + Add
            </button>
            {QUICK_BUMPS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => bumpToday.mutate(deck.extraNewToday + n)}
                disabled={bumpToday.isPending}
                className="btn-secondary text-sm"
              >
                +{n}
              </button>
            ))}
            <button
              type="button"
              onClick={() => bumpToday.mutate(0)}
              disabled={bumpToday.isPending || deck.extraNewToday === 0}
              className="btn-secondary text-sm"
            >
              Reset
            </button>
          </div>
        </div>
      </section>

      <section className="pt-4 border-t border-slate-200 dark:border-slate-800 space-y-2">
        <div className="text-xs uppercase tracking-wide text-slate-500">Danger zone</div>
        <button
          onClick={() => {
            if (
              confirm(
                `Delete deck "${deck.name}" and all ${deck.noteCount} card${
                  deck.noteCount === 1 ? "" : "s"
                }? This cannot be undone.`,
              )
            ) {
              deleteDeck.mutate();
            }
          }}
          disabled={deleteDeck.isPending}
          className="text-rose-500 hover:text-rose-600 dark:text-rose-400 dark:hover:text-rose-300 text-sm disabled:opacity-50"
        >
          Delete this deck
        </button>
      </section>
    </div>
  );
}
